import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Bell, Calendar as CalIcon, Globe, Menu, X, LogOut } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import SessionIndicator, { ExtendSessionMenuItem } from "./SessionIndicator";
import HeaderSearch from "./HeaderSearch";

const NAV = [
  { to: "/dashboard", key: "play_now" },
  { to: "/matches", key: "matches" },
  { to: "/tournaments", key: "tournaments" },
  { to: "/leagues", key: "leagues" },
  { to: "/lessons", key: "lessons" },
  { to: "/clubs", key: "clubs" },
  { to: "/facilities", key: "facilities" },
  { to: "/rankings", key: "rankings" },
];

export default function AppHeader() {
  const { user, logout } = useAuth();
  const { t, lang, setLang, languages } = useI18n();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const initials = user
    ? ((user.first_name?.[0] || user.email?.[0] || "U") + (user.last_name?.[0] || "")).toUpperCase()
    : "";

  return (
    <header
      className="pp-glass-nav sticky top-0 z-40"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
      data-testid="app-header"
    >
      <div
        className="max-w-7xl mx-auto h-16 flex items-center justify-between gap-4"
        style={{
          paddingLeft: "max(1rem, env(safe-area-inset-left))",
          paddingRight: "max(1rem, env(safe-area-inset-right))",
        }}
      >
        <Link to="/" data-testid="header-logo-link"><Logo size={36} /></Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="header-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "text-white bg-white/5" : "text-zinc-400 hover:text-white hover:bg-white/5"}`
              }
            >
              {t(n.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden md:block">
            <HeaderSearch />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="lang-trigger" className="pp-btn-secondary flex items-center gap-2 px-3 py-2 rounded-lg text-sm">
                <Globe size={16} /> <span className="hidden sm:inline">{lang.toUpperCase()}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#131313] border-white/10 text-white">
              <DropdownMenuLabel>{t("language")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {languages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`cursor-pointer ${l.code === lang ? "text-[#00E676]" : ""}`}
                >
                  {l.name} <span className="ms-auto text-xs text-zinc-500">{l.code.toUpperCase()}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <>
              <Link to="/notifications" data-testid="header-notifications" className="hidden sm:inline-flex pp-btn-secondary p-2 rounded-lg">
                <Bell size={18} />
              </Link>
              <Link to="/calendar" data-testid="header-calendar" className="hidden sm:inline-flex pp-btn-secondary p-2 rounded-lg">
                <CalIcon size={18} />
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="ms-1 relative">
                    <Avatar className="h-9 w-9 border border-white/10">
                      <AvatarImage src={user.avatar_url || ""} />
                      <AvatarFallback className="bg-[#00E676] text-black font-bold">{initials}</AvatarFallback>
                    </Avatar>
                    <span className="absolute -bottom-0.5 -right-0.5">
                      <SessionIndicator />
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#131313] border-white/10 text-white w-56">
                  <DropdownMenuLabel>{user.first_name} {user.last_name} <span className="ms-2 text-xs text-zinc-500">{user.role}</span></DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile" className="cursor-pointer">{t("profile")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/me/passport")} data-testid="menu-passport" className="cursor-pointer">Passport</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/calendar")} data-testid="menu-calendar" className="cursor-pointer">{t("calendar")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/invoices")} data-testid="menu-invoices" className="cursor-pointer">{t("invoices")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings" className="cursor-pointer">{t("settings")}</DropdownMenuItem>
                  {user.role === "coach" && <DropdownMenuItem onClick={() => navigate("/coach")} data-testid="menu-coach-dashboard" className="cursor-pointer">{t("dashboard")} (Coach)</DropdownMenuItem>}
                  {user.role === "club_admin" && <DropdownMenuItem onClick={() => navigate("/club")} data-testid="menu-club-dashboard" className="cursor-pointer">{t("dashboard")} (Club)</DropdownMenuItem>}
                  {user.role === "federation" && <DropdownMenuItem onClick={() => navigate("/federation")} data-testid="menu-federation" className="cursor-pointer">{t("federation")}</DropdownMenuItem>}
                  {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin" className="cursor-pointer">{t("admin")}</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <ExtendSessionMenuItem />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout" className="cursor-pointer text-[#FF3B30]">
                    <LogOut size={14} className="me-2" /> {t("logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login" className="pp-btn-secondary px-3 py-2 rounded-lg text-sm">{t("login")}</Link>
              <Link to="/register" data-testid="header-register" className="pp-btn-primary px-3 py-2 rounded-lg text-sm hidden sm:inline-flex">{t("register")}</Link>
            </>
          )}

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden pp-btn-secondary p-2 rounded-lg"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {mobileOpen ? (
        <div className="lg:hidden px-4 pb-4 border-t border-white/5" data-testid="mobile-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.key}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-3 rounded-lg text-sm ${isActive ? "text-white bg-white/5" : "text-zinc-400"}`
              }
            >
              {t(n.key)}
            </NavLink>
          ))}
        </div>
      ) : null}
    </header>
  );
}
